import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import Logo from "./Logo";

export default function Footer() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/contact-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const c = info || {
    organization: "Oblastný futbalový zväz Levice",
    address: "Podjavorinskej 3, 934 01 Levice",
    email: "sekretar@obfz-levice.sk",
    phone: "0903 384 831",
    facebook_url: "https://www.facebook.com/obfzlevice",
    futbalnet_url: "https://sportnet.sme.sk/futbalnet/z/obfz-levice/s/viiliga-obfz-levice/",
  };

  return (
    <footer className="relative bg-green-950 text-white mt-20 overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 pitch-grid opacity-40 pointer-events-none" />
      <div className="wrap relative">
        {/* Top banner */}
        <div className="py-14 md:py-20 border-b border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow-light mb-5">Oficiálny partner regiónu</div>
              <h2 className="font-heading font-extrabold text-white text-4xl md:text-6xl tracking-tight leading-[0.95]">
                Futbal, ktorý<br/>spája Levický okres.
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <a
                href={c.futbalnet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-white btn-lg"
              >
                Sledovať Futbalnet <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Columns */}
        <div className="py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Logo size={48} bg="#ECFBF2" color="#073B22" />
              <div className="leading-tight font-heading font-extrabold text-xl">
                ObFZ<br/>Levice
              </div>
            </div>
            <p className="text-sm text-white/70 max-w-xs">
              Oficiálne informácie, súťaže, rozpisy a oznamy pre oblastný futbal
              v levickom regióne.
            </p>
          </div>

          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/60 mb-4">Odkazy</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/uradne-spravy" className="text-white/90 hover:text-white">Úradné správy</Link></li>
              <li><Link to="/sutaze" className="text-white/90 hover:text-white">Súťaže</Link></li>
              <li><Link to="/rozpis" className="text-white/90 hover:text-white">Rozpis 25/26</Link></li>
              <li><Link to="/rozhodcovia" className="text-white/90 hover:text-white">Rozhodcovia</Link></li>
              <li><Link to="/dokumenty" className="text-white/90 hover:text-white">Dokumenty</Link></li>
              <li><a href="https://stara-wbhs.obfz-levice.sk/" target="_blank" rel="noopener noreferrer" className="text-white/90 hover:text-white inline-flex items-center gap-1" data-testid="footer-old-site-link">Stará stránka <ArrowUpRight className="w-3.5 h-3.5" /></a></li>
            </ul>
          </div>

          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/60 mb-4">Kontakt</div>
            <ul className="space-y-2.5 text-sm text-white/90">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-100" />
                <span>{c.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 flex-shrink-0 text-green-100" />
                <a href={`mailto:${c.email}`} className="hover:text-white">{c.email}</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 flex-shrink-0 text-green-100" />
                <a href={`tel:${c.phone}`} className="hover:text-white">{c.phone}</a>
              </li>
            </ul>
          </div>

          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/60 mb-4">Externé</div>
            <ul className="space-y-2.5 text-sm">
              <li><a href={c.futbalnet_url} target="_blank" rel="noopener noreferrer" className="text-white/90 hover:text-white inline-flex items-center gap-1">Futbalnet <ArrowUpRight className="w-3.5 h-3.5" /></a></li>
              <li><a href={c.facebook_url} target="_blank" rel="noopener noreferrer" className="text-white/90 hover:text-white inline-flex items-center gap-1"><Facebook className="w-3.5 h-3.5" /> Facebook</a></li>
              <li><a href="https://my.sportnet.online/issf/" target="_blank" rel="noopener noreferrer" className="text-white/90 hover:text-white inline-flex items-center gap-1">ISSF <ArrowUpRight className="w-3.5 h-3.5" /></a></li>
              <li><a href="https://www.futbalsfz.sk/" target="_blank" rel="noopener noreferrer" className="text-white/90 hover:text-white inline-flex items-center gap-1">SFZ <ArrowUpRight className="w-3.5 h-3.5" /></a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/60 mono tracking-wider">
          <span>© {new Date().getFullYear()} {c.organization?.toUpperCase()} · VŠETKY PRÁVA VYHRADENÉ</span>
          <div className="flex items-center gap-4">
            <span>Sekretariát: <a href={`mailto:${c.email}`} className="hover:text-white">{c.email}</a></span>
            <Link to="/admin/login" data-testid="admin-login-link"
                  className="text-white/30 hover:text-white/70 transition-colors">
              Prihlásenie
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
